import React from 'react';
import { parseForm, type FormElement } from '../../utils/formParser';

interface FormViewProps {
  /** Markdown content to render */
  markdown: string;
  /** Current form data */
  formData: Record<string, unknown>;
  /** Called when any field changes */
  onChange: (data: Record<string, unknown>) => void;
  /** Called when save button is clicked (optional, for manual save) */
  onSave?: () => void;
  /** Whether the form is read-only */
  readOnly?: boolean;
  /** Auto-save on checkbox changes (default: true) */
  autoSaveCheckboxes?: boolean;
  /** Show save button (default: true if onSave provided) */
  showSaveButton?: boolean;
  /** Save button is loading */
  isSaving?: boolean;
  /** Show progress header (default: true) */
  showProgress?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FormView - Renders markdown with interactive form elements
 *
 * This is a pure view component that handles rendering and local state changes.
 * Storage and persistence are handled by the parent component through callbacks.
 *
 * Supports:
 * - Headers (### Title)
 * - Checkboxes (- [ ] Task)
 * - Range fields (@field name type=range min=1 max=10)
 * - Number fields (@field name type=number unit=kg)
 * - Text fields (@field name type=text)
 * - Text areas (@text name rows=3)
 * - Math expressions ($inline$ or $$block$$)
 * - Regular text
 */
export function FormView({
  markdown,
  formData,
  onChange,
  onSave,
  readOnly = false,
  autoSaveCheckboxes = true,
  showSaveButton = !!onSave,
  isSaving = false,
  showProgress = true,
  className = '',
}: FormViewProps) {
  // Parse markdown
  const schema = parseForm(markdown);

  const updateField = (key: string, value: unknown) => {
    const newData = {
      ...formData,
      [key]: value,
    };
    onChange(newData);
  };

  const renderElement = (element: FormElement) => {
    switch (element.type) {
      case 'header':
        const HeaderTag = `h${Math.min(element.level, 6)}` as keyof JSX.IntrinsicElements;
        const headerClasses = {
          1: 'text-2xl font-bold text-gray-900 mt-6 mb-4',
          2: 'text-xl font-bold text-gray-900 mt-5 mb-3',
          3: 'text-lg font-semibold text-gray-800 mt-4 mb-3',
          4: 'text-base font-semibold text-gray-800 mt-3 mb-2',
          5: 'text-sm font-semibold text-gray-700 mt-2 mb-2',
          6: 'text-sm font-semibold text-gray-600 mt-2 mb-1',
        };
        return (
          <HeaderTag key={element.id} className={headerClasses[element.level as keyof typeof headerClasses] || headerClasses[3]}>
            {element.text}
          </HeaderTag>
        );

      case 'checkbox':
        const checkboxKey = `checkbox_${element.index}`;
        const isChecked = formData[checkboxKey] === true;
        return (
          <div key={element.id} className="flex items-start gap-2 mb-2">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => updateField(checkboxKey, e.target.checked)}
              disabled={readOnly || (autoSaveCheckboxes && isSaving)}
              className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            />
            <label
              className={`flex-1 text-sm cursor-pointer select-none ${
                isChecked ? 'text-gray-400 line-through' : 'text-gray-700'
              } ${readOnly ? 'cursor-default' : ''}`}
              onClick={() => !readOnly && updateField(checkboxKey, !isChecked)}
            >
              {element.text}
            </label>
          </div>
        );

      case 'field':
        const fieldValue = formData[element.id];

        if (element.fieldType === 'range') {
          const value = typeof fieldValue === 'number' ? fieldValue : element.defaultValue || element.min || 1;
          return (
            <div key={element.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {element.label}
                {!element.optional && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={element.min || 1}
                  max={element.max || 10}
                  value={value}
                  onChange={(e) => updateField(element.id, parseFloat(e.target.value))}
                  disabled={readOnly || isSaving}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-sm font-medium text-gray-700 min-w-[2rem] text-right">
                  {value}
                </span>
              </div>
            </div>
          );
        }

        if (element.fieldType === 'number') {
          const value = typeof fieldValue === 'number' ? fieldValue : element.defaultValue || '';
          return (
            <div key={element.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {element.label}
                {!element.optional && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={element.min}
                  max={element.max}
                  value={value}
                  onChange={(e) => updateField(element.id, e.target.value ? parseFloat(e.target.value) : undefined)}
                  disabled={readOnly || isSaving}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={element.optional ? 'Optional' : ''}
                />
                {element.unit && (
                  <span className="text-sm text-gray-500">{element.unit}</span>
                )}
              </div>
            </div>
          );
        }

        if (element.fieldType === 'counter') {
          const value = typeof fieldValue === 'number' ? fieldValue : element.defaultValue || element.min || 0;
          const min = element.min ?? 0;
          const max = element.max ?? 100;

          return (
            <div key={element.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {element.label}
                {!element.optional && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => updateField(element.id, Math.max(min, value - 1))}
                  disabled={readOnly || isSaving || value <= min}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-semibold text-gray-900 min-w-[3rem] text-center">
                    {value}
                  </span>
                  {element.unit && (
                    <span className="text-sm text-gray-500">{element.unit}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => updateField(element.id, Math.min(max, value + 1))}
                  disabled={readOnly || isSaving || value >= max}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          );
        }

        // text field
        return (
          <div key={element.id} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
              {!element.optional && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={(fieldValue as string) || ''}
              onChange={(e) => updateField(element.id, e.target.value)}
              disabled={readOnly || isSaving}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        );

      case 'goalfield':
        const goalRawValue = formData[element.id];
        const goalValue = typeof goalRawValue === 'number' ? goalRawValue : (element.defaultValue || 0);
        const goalInputValue = goalRawValue === undefined || goalRawValue === null ? '' : String(goalRawValue);
        const goalReached = goalValue >= element.goal;
        const goalCheckboxKey = `${element.id}_completed`;
        const useNumberInput = element.goal > 20;

        return (
          <div key={element.id} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
              {!element.optional && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {useNumberInput ? (
                /* Number input for goals > 20 */
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={goalInputValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        updateField(element.id, undefined);
                      } else {
                        const num = parseFloat(val);
                        if (!isNaN(num)) {
                          updateField(element.id, num);
                        }
                      }
                    }}
                    disabled={readOnly || isSaving}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-500">
                    / {element.goal} {element.unit || ''}
                  </span>
                </div>
              ) : (
                /* +/- buttons for goals <= 20 */
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => updateField(element.id, Math.max(0, goalValue - 1))}
                    disabled={readOnly || isSaving || goalValue <= 0}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-semibold text-gray-900 min-w-[3rem] text-center">
                      {goalValue}
                    </span>
                    <span className="text-sm text-gray-500">
                      / {element.goal} {element.unit || ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateField(element.id, goalValue + 1)}
                    disabled={readOnly || isSaving}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              )}
              {/* Progress bar */}
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    goalReached ? 'bg-green-500' : 'bg-primary-600'
                  }`}
                  style={{ width: `${Math.min(100, (goalValue / element.goal) * 100)}%` }}
                />
              </div>
              {/* Auto checkbox when goal reached */}
              {goalReached && (
                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={formData[goalCheckboxKey] === true}
                    onChange={(e) => updateField(goalCheckboxKey, e.target.checked)}
                    disabled={readOnly}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                  />
                  <label className="flex-1 text-sm font-medium text-green-800">
                    Goal reached! ({goalValue} / {element.goal} {element.unit || ''})
                  </label>
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        );

      case 'textarea':
        const textValue = (formData[element.id] as string) || '';
        return (
          <div key={element.id} className="mb-4">
            {element.label && (
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {element.label}
                {!element.optional && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <textarea
              rows={element.rows}
              value={textValue}
              onChange={(e) => updateField(element.id, e.target.value)}
              disabled={readOnly || isSaving}
              placeholder={element.placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        );

      case 'computed':
        // Computed fields are read-only derived metrics
        const computedValue = formData[element.id];
        const displayValue = computedValue !== undefined && computedValue !== null
          ? String(computedValue)
          : '—';

        // Format based on kind
        const getKindLabel = (kind: string) => {
          switch (kind) {
            case 'rolling_avg': return 'Average';
            case 'window_agg': return 'Total';
            case 'delta': return 'Change';
            case 'compliance': return 'Compliance';
            case 'streak': return 'Streak';
            case 'status': return 'Status';
            default: return 'Computed';
          }
        };

        const getKindIcon = (kind: string) => {
          switch (kind) {
            case 'rolling_avg':
              return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />;
            case 'streak':
              return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />;
            case 'compliance':
              return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />;
            case 'delta':
              return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />;
            default:
              return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />;
          }
        };

        return (
          <div key={element.id} className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {getKindIcon(element.kind)}
                </svg>
                <div>
                  <span className="text-sm font-medium text-gray-700">{element.label}</span>
                  <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                    {getKindLabel(element.kind)}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-lg font-semibold text-gray-900">{displayValue}</span>
                {element.unit && (
                  <span className="ml-1 text-sm text-gray-500">{element.unit}</span>
                )}
              </div>
            </div>
            {element.window && (
              <div className="mt-1 text-xs text-gray-500">
                Window: {element.window}
              </div>
            )}
          </div>
        );

      case 'math':
        // For now, render as code block - can be enhanced with KaTeX later
        if (element.inline) {
          return (
            <span key={element.id} className="inline-block px-2 py-1 bg-gray-100 rounded font-mono text-sm">
              {element.content}
            </span>
          );
        }
        return (
          <div key={element.id} className="my-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <code className="block text-center font-mono text-sm">{element.content}</code>
          </div>
        );

      case 'text':
        return (
          <p key={element.id} className="text-sm text-gray-700 mb-2">
            {element.content}
          </p>
        );

      default:
        return null;
    }
  };

  // Calculate completion stats for checkboxes
  const checkboxElements = schema.elements.filter(e => e.type === 'checkbox') as Array<FormElement & { type: 'checkbox' }>;
  const completedCheckboxes = checkboxElements.filter(
    e => formData[`checkbox_${e.index}`] === true
  ).length;

  return (
    <div className={className}>
      {/* Progress bar for checkboxes */}
      {showProgress && checkboxElements.length > 0 && (
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
          <div className="text-xs text-gray-500">
            {completedCheckboxes} of {checkboxElements.length} completed
          </div>
          <div className="flex-1 max-w-[200px] h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 transition-all duration-300"
              style={{ width: `${checkboxElements.length > 0 ? (completedCheckboxes / checkboxElements.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Form elements */}
      <div className="space-y-1">
        {schema.elements.map((element) => renderElement(element))}
      </div>

      {/* Auto-save indicator (save button removed - all changes auto-save) */}
      {isSaving && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          <span>Saving...</span>
        </div>
      )}
    </div>
  );
}
